import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "@/components/ui/toast";
import { useAuth } from "@/lib/auth/store";
import type { User } from '@/types';
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { Home } from "./pages/Home";
import { Experiences } from "./pages/Experiences";
import { Creators } from "./pages/Creators";
import { CreatorProfile } from "./pages/creators/[id]";
import { About } from "./pages/About";
import { Apply } from "./pages/Apply";
import { Settings } from "./pages/profile/Settings";
import { Profile } from "./pages/profile/Profile";
import AdminPage from "./pages/admin";
import { CreatorDashboard } from "./pages/creator/Dashboard";
import { CreateExperience } from "./pages/creator/CreateExperience";
import { EditExperience } from "./pages/creator/EditExperience";
import { ExperiencesList } from "./pages/creator/ExperiencesList";
import { BookExperience } from "./pages/experiences/[id]/book";
import { ExperienceDetails } from "./pages/experiences/[id]";
import { AzoresExperience } from "./pages/experiences/azores";
import { Bookings } from "./pages/Bookings";
import { Privacy } from "./pages/legal/Privacy";
import { Terms } from "./pages/legal/Terms";
import { Cookies } from "./pages/legal/Cookies";
import { AuthCallback } from "./pages/auth/Callback";
import { Azores } from "./pages/Azores";
import { Lucido as LucidoApril } from "./pages/Lucido-April-12";
// Import the static experience components
import { AyuniExperience } from "./pages/experiences/ayuni";
import { ArtHouseExperience } from "./pages/experiences/arthouse";
import { ArtHouseMay22 } from "./pages/experiences/arthouse-may-22";
import { AegeanExperience } from "./pages/experiences/aegean";
import { AegeanOdysseyVoyage2 } from "./pages/experiences/aegean2";
import { JoieDeVivreExperience } from "./pages/experiences/joiedevivre";
import { SunscapesExperience } from "./pages/experiences/sunscapes";
import { XamanicExperience } from "./pages/experiences/xamanic";
import { MetaTags } from '@/components/MetaTags';
import { supabase } from './lib/supabase/client';
// Removed deprecated mainStore import
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuthSessionRestore } from '@/lib/auth/useAuthSessionRestore';

// Protected route guard component
function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: string | string[];
}) {
  const { user, loading } = useAuth();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  // Show loading state only on initial load
  if (loading && isInitialLoad) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sand-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sand-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!loading && !user) {
    return <Navigate to="/login" replace />;
  }

  // Check role requirements
  if (user && requiredRole) {
    const allowedRoles = Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole];

    if (!allowedRoles.includes(user.role) && user.role !== "admin") {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

function App() {
  // Always call hooks at the top, before any return
  useAuthSessionRestore();
  const { user, loading } = useAuth();
  // Removed deprecated setUser from mainStore

  // User synchronization is now handled by the auth system
  // No need for manual store synchronization

  if (loading) {
    return <LoadingSpinner />;
  }

  // Expose for debugging
  if (typeof window !== 'undefined') {
    window.supabase = supabase;
    window.useAuth = useAuth;
  }

  return (
    <HelmetProvider>
      <MetaTags 
        title="rendezvous - Curated Experiences"
        description="Discover extraordinary moments through curated experiences that inspire, transform, and connect."
        image="https://rendezvouswebsite.b-cdn.net/DSCF9542.JPG"
        url="https://letsrendezvous.co"
      />

      <Router>
        <div className="min-h-screen bg-earth-900 text-sand-50">
          <Navigation />
          <ToastContainer />
          <main>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/experiences" element={<Experiences />} />
              {/* Static experience routes */}
              <Route
                path="/experiences/azores"
                element={<AzoresExperience />}
              />
              <Route
                path="/experiences/ayuni"
                element={<AyuniExperience />}
              />
              <Route
                path="/experiences/arthouse"
                element={<ArtHouseExperience />}
              />
              <Route
                path="/experiences/arthouse-may-22"
                element={<ArtHouseMay22 />}
              />
              <Route
                path="/experiences/aegean"
                element={<AegeanExperience />}
              />
              <Route
                path="/experiences/aegean2"
                element={<AegeanOdysseyVoyage2 />}
              />
              <Route
                path="/experiences/joiedevivre"
                element={<JoieDeVivreExperience />}
              />
              <Route
                path="/experiences/sunscapes"
                element={<SunscapesExperience />}
              />
              <Route
                path="/experiences/xamanic"
                element={<XamanicExperience />}
              />
              {/* Dynamic experience route - must be after static routes */}
              <Route path="/experiences/:id" element={<ExperienceDetails />} />
              <Route
                path="/experiences/:id/book"
                element={<BookExperience />}
              />
              <Route path="/creators" element={<Creators />} />
              <Route path="/creators/:id" element={<CreatorProfile />} />
              <Route path="/about" element={<About />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/apply/:type" element={<Apply />} />
              <Route path="/azores" element={<Azores />} />
              <Route path="/lucido-april-12" element={<LucidoApril />} />

              {/* Protected Routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute>
                    <Bookings />
                  </ProtectedRoute>
                }
              />

              {/* Creator Routes */}
              <Route
                path="/creator/dashboard"
                element={
                  <ProtectedRoute requiredRole="creator">
                    <CreatorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creator/experiences/new"
                element={
                  <ProtectedRoute requiredRole={["creator", "admin"]}>
                    <CreateExperience />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creator/experiences/:id/edit"
                element={
                  <ProtectedRoute requiredRole="creator">
                    <EditExperience />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/creator/experiences"
                element={
                  <ProtectedRoute requiredRole="creator">
                    <ExperiencesList />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Legal Routes */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;

declare global {
  interface Window {
    supabase: typeof supabase;
    useAuth: typeof useAuth;
  }
}